<template>
    <Screen
        v-if="course"
        :screen="startScreen"
        :course="course"
        :selected="0"
        background
    />
    <form
        :class="viewport.is"
        @submit.prevent="submit(false)"
    >
        <template v-if="isCheckingCode">
            <Txt size="s" heading>
                One last step!
            </Txt>
            <Txt size="s">
                Check your inbox for a verification code sent to {{ email }}
            </Txt>
            <Field
                ref="code"
                v-model:value="code"
                type="number"
                name="code"
                required
                inline-label
                show-info-always
                autofocus
                :strict="false"
                :show-controls="false"
                @valid="isValid = $event"
            >
                Enter 6 digit verification code
                <template #error>
                    <Txt v-if="status === 'error'" size="xxs">
                        <Icon name="danger" size="16" class="text-danger" />
                        The code provided was invalid.
                    </Txt>
                </template>
            </Field>
            <Button
                type="submit"
                colour="smoke"
                size="xxs" heading
                fill
                :disabled="!isValid"
                :status="status"
            >
                Verify your email
            </Button>
            <Txt size="xs" class="anchor">
                Can’t find it? Make sure you check your spam folder. <br />
                <a href="#" @click="submit(true)">Resend code</a> or <a href="#" @click="back">Try another email</a>
            </Txt>
        </template>
        <template v-else>
            <Txt v-if="course" size="s" heading>
                A course is waiting.
            </Txt>
            <Txt v-else size="s" heading>
                A collection is waiting
            </Txt>
            <Txt v-if="course" size="s">
                The creator of this course requires an email address for you to access it. You will be sent a code to confirm your email.
            </Txt>
            <Txt v-else size="s">
                The creator of this collection requires an email address for you to access it. You will be sent a code to confirm your email.
            </Txt>
            <Field
                v-model:value="email"
                type="email"
                name="email"
                autocomplete="email"
                placeholder="name@email.com"
                show-info-always
                inline-label
                required
                @valid="isValid = $event"
                @update:value="isEmailError = false"
            >
                Enter email address
                <template #info>
                    <Txt v-if="isEmailError" size="xxs">
                        <Icon name="danger" size="16" class="text-danger" />
                        This email does not have access to this course.
                    </Txt>
                </template>
            </Field>
            <Button
                type="submit"
                colour="smoke"
                size="xs" heading
                fill
                :disabled="!isValid"
                :status="status"
            >
                Continue
            </Button>
            <Txt size="xs" class="anchor">
                By providing your email, you agree to our 
                <a href="https://coassemble.com/privacy-policy" target="_blank">
                    Privacy Policy
                </a>
            </Txt>
        </template>
    </form>
</template>

<script>
import { startScreen } from '@/helpers/course';
import { useResponsive } from '@/store/responsive';

import Txt from '@/components/form/txt';
import Field from '@/components/form/field';
import Icon from '@/components/utility/icon';
import Screen from '@/components/author/screen';
import Button from '@/components/utility/button';

export default {
    name: 'CourseAccess',
    components: { Txt, Field, Icon, Screen, Button },
    props: {
        course: { type: Object, default: null },
        collection: { type: Object, default: null }
    },
    emits: ['authorised'],
    data() {
        return {
            email: '',
            code: '',
            
            status: null,
            isValid: false,
            isEmailError: false,
            isCheckingCode: false,

            viewport: useResponsive()
        };
    },
    computed: {
        key() {
            return this.$route.params.key
                || this.$route.params.collectionKey
                || '';
        },
        keyType() {
            return this.key.length === 32 ? 'key' : 'id';
        },
        startScreen() {
            return startScreen(this.course);
        },
    },
    methods: {
        submit(resend = false) {
            if (this.isCheckingCode && !resend) return this.checkCode();
            if (this.status) return;
            this.status = 'waiting';
            return this.$http
                .get(`/learn/auth/${this.key}`, { params: { email: this.email, collection: !!this.collection } })
                .then(({ data }) => {
                    if (data) {
                        this.status = null;
                        this.isValid = false;
                        this.isCheckingCode = true;
                    } else {
                        this.status = 'error';
                        this.isEmailError = true;
                        this.$timeout(() => this.status = null, 2000);
                    }
                });
        },
        checkCode() {
            if (this.status) return;
            this.status = 'waiting';
            return this.$http
                .get(`/learn/auth/${this.key}`, { params: { email: this.email, code: this.code, collection: !!this.collection } })
                .then(({ data }) => {
                    if (data.token) {
                        this.status = 'success';
                        this.$emit('authorised', data.token);
                    } else {
                        this.$refs.code.focus();
                        this.status = 'error';
                        this.$timeout(() => this.status = null, 2000);
                    }
                });
        },
        back() {
            this.isCheckingCode = false;
            this.isValid = false;
            this.email = '';
            this.status = null;
        }
    }
};
</script>

<style lang="less" scoped>
.screen {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
}
form {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin: auto;
    width: 460px;
    max-width: calc(100vw - 48px);
    background-color: #FFF;
    border-radius: 16px;
    padding: 32px;
    box-shadow: var(--shadow-base);
    .txt-component.heading.s { margin-bottom: 12px; }
    .field {
        margin-top: 24px;
        width: 100%;
    }
    .cobutton {
        margin-bottom: 16px;
    }
    &.mobile {
        padding: 32px 24px;
    }
}
</style>
